import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ShieldCheck, AlertTriangle, Clock, FileWarning, Landmark, Banknote, Paperclip, Users } from "lucide-react";
import { DocumentRecord, InventoryItem } from "@/hooks/useDocuments";
import { ComplianceResult } from "@/hooks/useComplianceLookup";
import { cn } from "@/lib/utils";
import { SupportDocumentUploadModal } from "./SupportDocumentUploadModal";

interface RiskRadarProps {
  documento: DocumentRecord;
  items: InventoryItem[];
  complianceResults: Record<number, ComplianceResult>;
  fechaLlegada?: string | null;
  embarqueId?: string;
  orgId?: string;
  siblingDocuments?: DocumentRecord[];
  onCertUploaded?: () => void;
  /** Cliente importador del embarque padre. Si es null/undefined => embarque huérfano. */
  embarqueClienteId?: string | null;
}

interface RiskFinding {
  id: string;
  severity: "critical" | "warning" | "success";
  icon: React.ReactNode;
  title: string;
  message: string;
  actionable?: boolean;
  blockedCodes?: string[];
}

const PAYMENT_KEYWORDS = ["USDT", "BTC", "CRYPTO", "WALLET", "EFECTIVO", "CASH"];

function evaluateRisks(
  doc: DocumentRecord,
  items: InventoryItem[],
  complianceResults: Record<number, ComplianceResult>,
  fechaLlegada?: string | null,
  siblingDocuments?: DocumentRecord[],
  embarqueId?: string,
  embarqueClienteId?: string | null,
): RiskFinding[] {
  const risks: RiskFinding[] = [];

  // 0. 🔴 NIVEL 0 — Embarque Huérfano (sin cliente asignado).
  // Bloquea liquidación y deja el embarque invisible en el Portal B2B.
  if (embarqueId && !embarqueClienteId) {
    risks.push({
      id: "embarque-huerfano",
      severity: "critical",
      icon: <Users className="h-4 w-4" />,
      title: "🔴 Embarque Huérfano — Sin Cliente",
      message:
        "Este embarque no tiene un Cliente importador asignado. Es invisible en el Portal B2B y no puede liquidarse. Asigne un cliente inmediatamente desde la cabecera del embarque.",
    });
  }

  // 1. Incoterm CIF
  if (doc.incoterm && doc.incoterm.toUpperCase().includes("CIF")) {
    risks.push({
      id: "incoterm-cif",
      severity: "warning",
      icon: <AlertTriangle className="h-4 w-4" />,
      title: "Peligro de Costos Ocultos",
      message:
        "Se detectó Incoterm CIF. Asegúrese de negociar previamente los gastos de llegada con el agente de carga para evitar cobros abusivos en destino.",
    });
  }

  // 2. Declaración Anticipada 48h — prioridad: ETA del embarque; fallback: fecha del documento
  const arrivalDate = (fechaLlegada != null && fechaLlegada !== "")
    ? new Date(fechaLlegada)
    : doc.fecha ? new Date(doc.fecha) : null;
  if (arrivalDate && !isNaN(arrivalDate.getTime())) {
    const now = new Date();
    const diffMs = arrivalDate.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours < 48) {
      risks.push({
        id: "declaracion-anticipada",
        severity: "critical",
        icon: <Clock className="h-4 w-4" />,
        title: "Alerta Decreto 659",
        message:
          diffHours < 0
            ? "La fecha de arribo ya pasó. Verifique que la Declaración Anticipada fue presentada a tiempo para evitar incautación."
            : `La Declaración Anticipada debe presentarse mínimo 48h antes de la llegada. Quedan ~${Math.round(diffHours)}h. Verifique los tiempos inmediatamente.`,
      });
    }
  }

  // 3. Descripciones Vagas
  const vagueItems = items.filter(
    (item) => item.descripcion && item.descripcion.trim().split(/\s+/).length < 3
  );
  if (vagueItems.length > 0) {
    const labels = vagueItems.map((i) => i.sku || `#${i.item_no}`).join(", ");
    risks.push({
      id: "descripcion-vaga",
      severity: "warning",
      icon: <FileWarning className="h-4 w-4" />,
      title: "Factura Ingenua Detectada",
      message: `Los ítems [${labels}] tienen descripciones muy cortas. La aduana exige detallar material, uso y marca. Pida corrección al proveedor.`,
    });
  }

  // 4. Vistos Buenos (reglas_cumplimiento)
  const blockedCodes: string[] = [];
  for (const [, result] of Object.entries(complianceResults)) {
    if (result.regla) {
      const entidad = (result.regla.entidad ?? "").toUpperCase();
      if (["INVIMA", "ICA", "RETIE", "ANDI", "MINCOMERCIO"].some((e) => entidad.includes(e))) {
        const code = result.matched_codigo || "";
        if (code && !blockedCodes.includes(code)) blockedCodes.push(code);
      }
    }
  }

  if (blockedCodes.length > 0) {
    // Check if sibling docs already have a "Certificado Técnico"
    const hasCert = (siblingDocuments ?? []).some(
      (d) => d.tipo_documento === "Certificado Técnico"
    );

    if (hasCert) {
      risks.push({
        id: "vistos-buenos-ok",
        severity: "success",
        icon: <ShieldCheck className="h-4 w-4" />,
        title: "Certificados Adjuntos",
        message: `✅ Se detectaron certificados de soporte vinculados a este embarque para las partidas [${blockedCodes.map((c) => c.replace(/(\d{4})(\d{2})(\d{2})(\d{2})/, "$1.$2.$3.$4")).join(", ")}].`,
      });
    } else {
      risks.push({
        id: "vistos-buenos",
        severity: "critical",
        icon: <Landmark className="h-4 w-4" />,
        title: "Bloqueo Aduanero",
        message: `Las partidas arancelarias [${blockedCodes.map((c) => c.replace(/(\d{4})(\d{2})(\d{2})(\d{2})/, "$1.$2.$3.$4")).join(", ")}] requieren certificado técnico o Visto Bueno previo. Mercancía no nacionalizable sin este requisito.`,
        actionable: true,
        blockedCodes,
      });
    }
  }

  // 5. Pagos Irregulares
  const extraStr = JSON.stringify(doc.datos_extra ?? {}).toUpperCase();
  const notasStr = (doc.notas_tecnicas ?? "").toUpperCase();
  const combined = extraStr + " " + notasStr;
  const found = PAYMENT_KEYWORDS.filter((kw) => combined.includes(kw));
  if (found.length > 0) {
    risks.push({
      id: "pagos-irregulares",
      severity: "critical",
      icon: <Banknote className="h-4 w-4" />,
      title: "Riesgo Cambiario",
      message:
        "Posible método de pago no oficial detectado. Recordatorio: Pagar fuera del mercado cambiario oficial genera multas del 100% del valor de la operación por lavado de activos.",
    });
  }

  // 6. Clasificación Arancelaria por IA
  const iaItems = items.filter((item) => item.hs_code_deducido_por_ia === true);
  if (iaItems.length > 0) {
    risks.push({
      id: "ia_deduction_risk",
      severity: "critical",
      icon: <ShieldAlert className="h-4 w-4" />,
      title: "Clasificación Arancelaria por IA",
      message:
        `${iaItems.length} ítem(s) tienen un HS Code sugerido por Inteligencia Artificial. Requiere revisión humana obligatoria antes de nacionalización.`,
    });
  }

  return risks;
}

export function RiskRadar({
  documento,
  items,
  complianceResults,
  fechaLlegada,
  embarqueId,
  orgId,
  siblingDocuments,
  onCertUploaded,
  embarqueClienteId,
}: RiskRadarProps) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCodes, setUploadCodes] = useState<string[]>([]);

  const risks = useMemo(
    () => evaluateRisks(documento, items, complianceResults, fechaLlegada, siblingDocuments, embarqueId, embarqueClienteId),
    [documento, items, complianceResults, fechaLlegada, siblingDocuments, embarqueId, embarqueClienteId]
  );

  const criticalCount = risks.filter((r) => r.severity === "critical").length;
  const warningCount = risks.filter((r) => r.severity === "warning").length;
  const hasRisks = risks.some((r) => r.severity !== "success");

  const handleUploadClick = (codes: string[]) => {
    setUploadCodes(codes);
    setUploadOpen(true);
  };

  return (
    <>
      <Card
        className={cn(
          "border transition-colors",
          hasRisks
            ? criticalCount > 0
              ? "border-destructive/50 bg-destructive/5"
              : "border-amber-400/50 bg-amber-50/50 dark:bg-amber-950/20"
            : "border-emerald-400/50 bg-emerald-50/50 dark:bg-emerald-950/20"
        )}
      >
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            {hasRisks ? (
              <ShieldAlert className={cn("h-4 w-4", criticalCount > 0 ? "text-destructive" : "text-amber-600 dark:text-amber-400")} />
            ) : (
              <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            )}
            <span>Risk Radar</span>
            {(criticalCount > 0 || warningCount > 0) && (
              <div className="flex gap-1 ml-auto">
                {criticalCount > 0 && (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                    {criticalCount} crítico{criticalCount > 1 ? "s" : ""}
                  </Badge>
                )}
                {warningCount > 0 && (
                  <Badge className="text-[10px] px-1.5 py-0 bg-amber-500 text-white hover:bg-amber-600">
                    {warningCount} alerta{warningCount > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 space-y-2">
          {risks.length === 0 ? (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              ✅ No se detectaron riesgos aduaneros en este documento. Todos los controles aprobados.
            </p>
          ) : (
            risks.map((risk) => {
              if (risk.severity === "success") {
                return (
                  <Alert
                    key={risk.id}
                    className="py-2 px-3 text-xs border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-600/40 dark:bg-emerald-950/30 dark:text-emerald-200"
                  >
                    <div className="flex items-start gap-2">
                      {risk.icon}
                      <div className="min-w-0">
                        <AlertTitle className="text-xs font-semibold mb-0.5">{risk.title}</AlertTitle>
                        <AlertDescription className="text-[11px] leading-snug">{risk.message}</AlertDescription>
                      </div>
                    </div>
                  </Alert>
                );
              }

              if (risk.severity === "critical") {
                return (
                  <Alert key={risk.id} variant="destructive" className="py-2 px-3 text-xs">
                    <div className="flex items-start gap-2">
                      {risk.icon}
                      <div className="min-w-0 flex-1">
                        <AlertTitle className="text-xs font-semibold mb-0.5">{risk.title}</AlertTitle>
                        <AlertDescription className="text-[11px] leading-snug">{risk.message}</AlertDescription>
                        {risk.actionable && embarqueId && orgId && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 h-7 text-[11px] border-destructive/30 hover:bg-destructive/10"
                            onClick={() => handleUploadClick(risk.blockedCodes ?? [])}
                          >
                            <Paperclip className="h-3 w-3 mr-1" />
                            Subir Certificado Soporte
                          </Button>
                        )}
                      </div>
                    </div>
                  </Alert>
                );
              }

              return (
                <Alert
                  key={risk.id}
                  className="py-2 px-3 text-xs border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-200 [&>svg]:text-amber-600"
                >
                  <div className="flex items-start gap-2">
                    {risk.icon}
                    <div className="min-w-0">
                      <AlertTitle className="text-xs font-semibold mb-0.5">{risk.title}</AlertTitle>
                      <AlertDescription className="text-[11px] leading-snug">{risk.message}</AlertDescription>
                    </div>
                  </div>
                </Alert>
              );
            })
          )}
        </CardContent>
      </Card>

      {embarqueId && orgId && (
        <SupportDocumentUploadModal
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          embarqueId={embarqueId}
          orgId={orgId}
          codigosPartida={uploadCodes}
          onUploaded={onCertUploaded ?? (() => {})}
        />
      )}
    </>
  );
}
